import * as React from 'react'

import {
  Box,
  Button,
  type SortDirection,
  Table,
  matchText,
} from '@factorialco/deprecated-design-system'
import orderBy from 'lodash/orderBy'

import { observer } from 'mobx-react'

import createComputed from 'modules/core/hooks/createComputed'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import LegalEntitySelector from 'modules/finance/components/Workspace/LegalEntitySelector'
import type { DimensionValue } from 'modules/finance/pages/DimensionDetailPage/DimensionDetail/useDimensionDetail'

import DimensionValuesTableBody from './DimensionValuesTableBody'
import Header, { columns } from './Header'
import TableOptions from './TableOptions'

interface Props {
  onAddDimension: () => void
  onDeleteValue: (valueId: number) => void
  onEditValue: (valueId: number) => void
  onLegalEntityChange: (id: string) => void
  onImport: () => void
  valueList: DimensionValue[]
}

const sortDimensionValues = createComputed(
  (dimensionValues: DimensionValue[], attribute: string, direction: 'asc' | 'desc') =>
    orderBy(
      dimensionValues,
      (value) => {
        switch (attribute) {
          case 'name': {
            return value.name.toLocaleLowerCase()
          }
          case 'legalEntity': {
            return value.legalEntity?.legalName!.toLocaleLowerCase()
          }
          case 'employees': {
            return value.customdimensionmembershipsConnection.totalCount
          }
          default: {
            return attribute
          }
        }
      },
      [direction]
    )
)

const filteredByFilterAndSearchBox = createComputed(
  (
    dimensionValuesList: DimensionValue[],
    legalEntityId: string | undefined,
    search: string,
    rowsText: Record<number, string>
  ) => {
    return dimensionValuesList.filter(
      (target) =>
        (!legalEntityId ||
          !target.legalEntity ||
          target.legalEntity.id === Number(legalEntityId)) &&
        matchText(search.toLocaleLowerCase(), rowsText[target.id] || '')
    )
  }
)

const rowsAsText = createComputed((dimensionValuesList: DimensionValue[]) => {
  return dimensionValuesList.reduce((acc, cur) => {
    acc[cur.id] = `${cur.name.toLowerCase()} ${cur.code?.toLocaleLowerCase()} ${
      cur.legalEntity?.legalName?.toLocaleLowerCase() ||
      i18n.t('finance.dimension_detail.table.all_legal_entities')
    }} ${cur.description?.toLocaleLowerCase()} ${
      cur.customdimensionmembershipsConnection.totalCount
    }`
    return acc
  }, {} as Record<number, string>)
})

const DimensionValuesTable = ({
  valueList,
  onAddDimension,
  onDeleteValue,
  onEditValue,
  onImport,
}: Props) => {
  const [sortDirection, setDirection] = React.useState<SortDirection>('asc')
  const [sortAttribute, setSortAttribute] = React.useState<string>(columns[0]?.attribute ?? '')
  const [legalEntityId, setLegalEntityId] = React.useState<string | undefined>(undefined)

  const onSort = (sortAttribute: string, sortDirection: SortDirection) => {
    setDirection(sortDirection)
    setSortAttribute(sortAttribute)
  }

  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const rowsText = rowsAsText(valueList).get()
  const sortedValues = sortDimensionValues(valueList, sortAttribute, sortDirection).get()
  const filteredValues = filteredByFilterAndSearchBox(
    sortedValues,
    legalEntityId,
    debouncedSearch,
    rowsText
  ).get()

  const hasCostCenters = filteredValues.length > 0

  const tableOptions = () => <TableOptions search={search} onSearch={onSearch} />

  const noCostCentersMessages =
    valueList.length > 0
      ? i18n.t('finance.dimension_detail.table.not_found')
      : i18n.t('finance.dimension_detail.table.no_records')

  return (
    <Box gap="s16">
      <Box flexDirection="row" justifyContent="spaceBetween">
        <LegalEntitySelector onChange={setLegalEntityId} disableMulticurrency />
        <Box flexDirection="row" gap="s12">
          <Button iconName="add" type="secondary" onClick={onImport}>
            {i18n.t('finance.dimension_detail.options.import_assignment')}
          </Button>
          <Button iconName="add" onClick={onAddDimension}>
            {i18n.t('finance.buttons.add_dimension_value')}
          </Button>
        </Box>
      </Box>
      <Table options={tableOptions} fullWidth>
        <Header onSort={onSort} direction={sortDirection} attribute={sortAttribute} />
        {hasCostCenters ? (
          <DimensionValuesTableBody
            dimensionValues={filteredValues}
            onDeleteValue={onDeleteValue}
            onEditValue={onEditValue}
          />
        ) : (
          <Table.Body>
            <Table.Blank colSpan={15} text={noCostCentersMessages} />
          </Table.Body>
        )}
      </Table>
    </Box>
  )
}

export default observer(DimensionValuesTable)
